import React, { MouseEventHandler, useCallback } from 'react'
import classNames from 'classnames'
import { setCookie } from 'cookies-next'
import { ButtonVariant, getVariantClasses } from '@/atoms/Button/ButtonUtil'
import { GUILD_SIGNUP_OFFER_ID_COOKIE, GUILD_SIGNUP_PROMO_CODE_COOKIE } from '@/constants/cookies'
import { paths } from '@/constants/paths'
import { getPriceForCurrency } from '@/services/PriceFormatter'
import { useUser } from '@/services/UserService'
import { MoneyCurrencyCode } from '@/types/codegen-federation'
import { useSafeTrack } from '@/utils/analytics'
import { useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import { useGuildSignupContext } from '@/views/GuildSignupView/GuildSignupContext'

interface Props {
  children: React.ReactNode
  currency?: string
  offerId: string
  price: number
  promoCode?: string | null
  planInterval: 'annual' | 'monthly'
  className?: string
  buttonVariant?: ButtonVariant
}

export const GuildSignupButton: React.FC<Props> = ({
  children,
  currency = 'USD',
  offerId,
  price,
  promoCode,
  planInterval,
  className,
  buttonVariant = 'core-gray-900',
}) => {
  const track = useSafeTrack()
  const { trackProductAdded } = useEcommerceEvents()
  const { slug } = useGuildSignupContext()
  const { isLoggedIn } = useUser()

  const handleGuildSignupStarted: MouseEventHandler<HTMLAnchorElement> = useCallback(() => {
    // TODO: deprecated - remove after release of trackProductAdded and marketing's (Kylo's) blessing
    track('Guild Checkout Started', {
      slug: slug,
      planId: offerId,
    })

    const formattedPrice = getPriceForCurrency(price, currency)
    trackProductAdded({
      category: 'guild',
      funnel: 'guild',
      name: `Angel Guild ${planInterval} - $${formattedPrice}`,
      price: formattedPrice,
      currency: currency as MoneyCurrencyCode,
      quantity: 1,
      product_id: offerId,
      sku: offerId,
      projectSlug: slug,
    })
    return
  }, [currency, track, slug, offerId, price, planInterval, trackProductAdded])

  return (
    <>
      <a
        className={classNames(
          'btn btn-standard',
          getVariantClasses({ variant: buttonVariant }),
          'w-full rounded-[10px] px-5 py-[11px]',
          className,
        )}
        href="#"
        onClick={(e) => {
          handleGuildSignupStarted(e)
          if (!isLoggedIn) {
            // Store offerId and promoCode for post-signup redirect
            setCookie(GUILD_SIGNUP_OFFER_ID_COOKIE, offerId)
            if (promoCode) {
              setCookie(GUILD_SIGNUP_PROMO_CODE_COOKIE, promoCode)
            }
            window.location.href = paths.guild.signup.createAccount
            e.preventDefault()
          } else {
            window.location.href = `/guild/checkout/${offerId}${promoCode ? `?promoCode=${promoCode}` : ''}`
            e.preventDefault()
          }
        }}
      >
        {children}
      </a>
    </>
  )
}
